<template>
  <v-autocomplete
    ref="autocompleteRef"
    v-model="internalValue"
    v-model:search="searchText"
    :data-testid="sectionTag"
    v-bind="$attrs"
    :label="getLabel"
    :base-color="tokens.color.input.border.default"
    :color="tokens.color.input.border.default"
    flat
    :menu-icon="mdiChevronDown"
    :menu-props="computedMenuProps"
    :hide-details="resolvedHideDetails"
    :class="{
      'no-label': !props.label,
      'extra-room': props.extraRoom,
      'w-dropdown--medium': props.size === 'medium' && !props.label && !isTextAppearance,
      'w-dropdown--text': isTextAppearance,
      'w-dropdown--multiple': isMultiple(),
      'w-dropdown--searching': Boolean(searchText)
    }"
  >
    <template v-if="$slots.label" #label="slotData">
      <slot name="label" v-bind="slotData || {}"></slot>
    </template>
    <template v-if="$slots.chip" #chip="slotData">
      <slot name="chip" v-bind="slotData || {}"></slot>
    </template>
    <template v-if="$slots.prepend" #prepend="slotData">
      <slot name="prepend" v-bind="slotData || {}"></slot>
    </template>
    <template v-if="$slots.append" #append="slotData">
      <slot name="append" v-bind="slotData || {}"></slot>
    </template>
    <template v-if="$slots.clear" #clear="slotData">
      <slot name="clear" v-bind="slotData || {}"></slot>
    </template>
    <template v-if="$slots.loader" #loader="slotData">
      <slot name="loader" v-bind="slotData || {}"></slot>
    </template>
    <template v-if="$slots['append-item']" #append-item>
      <slot name="append-item"></slot>
    </template>
    <template v-if="$slots['menu-footer']" #menu-footer="slotData">
      <slot name="menu-footer" v-bind="slotData || {}"></slot>
    </template>
    <template v-if="$slots['no-data']" #no-data>
      <slot name="no-data"></slot>
    </template>
    <template #item="{ item, props: itemProps }">
      <slot v-if="$slots.item" name="item" :item="item" :props="itemProps"></slot>
      <WAutocompleteListItem
        v-else
        :item="item"
        :item-props="itemProps"
        :item-subtitle="props.itemSubtitle"
        :item-title="getItemTitle()"
        :item-value="getItemValue()"
        :model-value="internalValue"
        :multiple="isMultiple()"
        :option-data-testid="props.optionDataTestid"
        :return-object="isReturnObject()"
        :section-tag="sectionTag"
        :value-comparator="getValueComparator()"
      >
        <template v-if="$slots['item-prepend']" #prepend="slotData">
          <slot name="item-prepend" v-bind="slotData"></slot>
        </template>
        <template v-if="$slots['item-append']" #append="slotData">
          <slot name="item-append" v-bind="slotData"></slot>
        </template>
      </WAutocompleteListItem>
    </template>
    <template #message="{ message }">
      <slot v-if="$slots.message" name="message" :message="message"></slot>
      <slot v-else>
        <v-icon
          :icon="mdiAlert"
          class="input-message-icon"
          :data-testid="sectionTag ? `${sectionTag}-error-icon` : undefined"
        ></v-icon>
        <span
          class="label-semibold-small text-red600 pa-0 ml-1"
          :data-testid="sectionTag ? `${sectionTag}-error-message` : undefined"
        >
          {{ message }}
        </span>
      </slot>
    </template>
    <template v-if="$slots['prepend-item'] || (selectAll && isMultiple())" #prepend-item>
      <slot v-if="$slots['prepend-item']" name="prepend-item"></slot>
      <template v-else>
        <WChecklistItem
          :aria-selected="isAllSelected()"
          :indeterminate="!isAllSelected() && selectedValues.length > 0"
          :model-value="isAllSelected()"
          :section-tag="sectionTag ? `${sectionTag}-select-all` : undefined"
          :selected="isAllSelected()"
          :title="$t('app.common.actions.select_all')"
          appearance="list-item"
          class="w-dropdown__select-all"
          role="option"
          selection-mode="delegated"
          tabindex="0"
          @keydown.enter.prevent="toggle"
          @keydown.space.prevent="toggle"
          @select="toggle"
        />
        <v-divider class="mt-2"></v-divider>
      </template>
    </template>
    <template #selection="{ item, index }">
      <slot v-if="$slots.selection" name="selection" :item="item" :index="index"></slot>
      <template v-else>
        <span v-if="showAllInline && index == 0" class="text-neutral1000">
          {{ inlineLabel }}:
          <span v-for="(value, indexItem) in selectedValues" :key="selectionKey(value, indexItem)">
            {{ value }}{{ indexItem < selectedValues.length - 1 ? ', ' : '' }}
          </span>
        </span>
        <span
          v-else-if="isMultiple() && index == 0"
          :data-testid="sectionTag ? `${sectionTag}-selection-${index}` : undefined"
        >
          {{ item.title }}
          <span v-if="selectedValues.length > 1"> +{{ selectedValues.length - 1 }}</span>
        </span>
        <span
          v-if="!isMultiple()"
          :class="{ 'label-semibold-small': isTextAppearance }"
          :data-testid="sectionTag ? `${sectionTag}-selection` : undefined"
        >
          {{ inlineLabel ? `${inlineLabel}: ${item.title}` : item.title }}
        </span>
      </template>
    </template>
    <template v-if="$slots['prepend-inner'] || (placeholder && isEmpty())" #prepend-inner="slotData">
      <slot v-if="$slots['prepend-inner']" name="prepend-inner" v-bind="slotData || {}"></slot>
      <span v-else class="body-regular-default text-neutral1000 placeholder-text">{{ placeholder }}</span>
    </template>
    <template v-if="$slots['append-inner']" #append-inner="slotData">
      <slot name="append-inner" v-bind="slotData || {}"></slot>
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { tokens } from '@/plugins/vuetify'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, watch } from 'vue'
import { mdiAlert, mdiChevronDown } from '@mdi/js'
import { $t } from '@/plugins/i18n'
import { appendOverlayContentClasses, mergeOverlayTokenStyles } from '@/components/wasabi/overlayStyles'
import type { OverlayContentClass, OverlayMenuStyle } from '@/components/wasabi/overlayStyles'
import WAutocompleteListItem from './WAutocompleteListItem.vue'
import WChecklistItem from './WChecklistItem.vue'
import type {
  WAutocompleteListItemDataTestid,
  WAutocompleteListItemModelValue,
  WAutocompleteListItemSubtitle,
  WAutocompleteListItemValueComparator
} from './WAutocompleteListItem.vue'
import {
  areAutocompleteValuesAllSelected,
  getAutocompleteSelectAllComparisonValues,
  getAutocompleteSelectAllValues
} from './autocompleteSelection'
import type { WAutocompleteItemKey } from './autocompleteSelection'

interface OverlayMenuProps extends Record<string, unknown> {
  contentClass?: OverlayContentClass
  style?: OverlayMenuStyle
}

type DropdownValue = WAutocompleteListItemModelValue
export type WDropdownSize = 'default' | 'medium'
/** `field` is the outlined input; `text` is a bare selection label + chevron with no field chrome. */
export type WDropdownAppearance = 'field' | 'text'

export interface WDropdownProps {
  modelValue?: DropdownValue
  sectionTag?: string
  label?: string
  required?: boolean
  menuProps?: OverlayMenuProps
  selectAll?: boolean
  inlineLabel?: string
  showAllInline?: boolean
  extraRoom?: boolean
  placeholder?: string
  itemSubtitle?: WAutocompleteListItemSubtitle
  optionDataTestid?: WAutocompleteListItemDataTestid
  size?: WDropdownSize
  appearance?: WDropdownAppearance
}

const props = withDefaults(defineProps<WDropdownProps>(), {
  modelValue: () => [],
  sectionTag: 'unspecified',
  label: undefined,
  required: false,
  menuProps: () => ({
    offset: 8,
    contentClass: 'v-autocomplete__content'
  }),
  selectAll: false,
  inlineLabel: '',
  showAllInline: false,
  extraRoom: false,
  placeholder: '',
  itemSubtitle: undefined,
  optionDataTestid: undefined,
  size: 'default',
  appearance: 'field'
})

const emit = defineEmits(['update:modelValue'])
const searchText = defineModel<string>('search', { default: '' })
const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value)
    if (isTextAppearance.value && lastInteractionWasPointer) void nextTick(() => autocompleteRef.value?.blur?.())
  }
})

const attrs = useAttrs() as Record<string, unknown> & {
  items?: unknown[]
  itemTitle?: WAutocompleteItemKey | null
  'item-title'?: WAutocompleteItemKey | null
  itemValue?: WAutocompleteItemKey | null
  'item-value'?: WAutocompleteItemKey | null
  multiple?: unknown
  returnObject?: unknown
  'return-object'?: unknown
  valueComparator?: unknown
  'value-comparator'?: unknown
  hideDetails?: boolean | 'auto'
  'hide-details'?: boolean | 'auto'
}
const autocompleteRef = ref<{ focus?: () => void; blur?: () => void; $el?: HTMLElement } | null>(null)

const isTextAppearance = computed(() => props.appearance === 'text')

// A text trigger chosen with the pointer drops focus once the choice is made, the way a native
// select does; otherwise the closed trigger would keep the keyboard-focus outline until the next
// click elsewhere. Keyboard-driven selection keeps focus so Tab order is unaffected. The menu is
// teleported out of this component, so a click or key press on an option never reaches its root:
// the last interaction is read at the document instead.
let lastInteractionWasPointer = false
const markPointerInteraction = () => {
  lastInteractionWasPointer = true
}
const markKeyboardInteraction = () => {
  lastInteractionWasPointer = false
}

// A text trigger reads as a label, so it takes no edits: Vuetify clears a single selection on
// Backspace or Delete, and typed or pasted text would become a hidden search that filters the menu.
// Both are stopped on the root in the capture phase, before the input's own handlers run. Listened
// for on the root element rather than through Vuetify's attr forwarding, which splits listeners
// between the root and the (hidden) input.
const blockSelectionClear = (event: KeyboardEvent) => {
  if (event.key !== 'Backspace' && event.key !== 'Delete') return
  event.preventDefault()
  event.stopPropagation()
}
const blockTextInput = (event: Event) => {
  event.preventDefault()
}

let textAppearanceRoot: HTMLElement | null = null

const removeTextAppearanceListeners = () => {
  document.removeEventListener('pointerdown', markPointerInteraction, true)
  document.removeEventListener('keydown', markKeyboardInteraction, true)
  textAppearanceRoot?.removeEventListener('keydown', blockSelectionClear, true)
  textAppearanceRoot?.removeEventListener('beforeinput', blockTextInput, true)
  textAppearanceRoot = null
}

// Only the text appearance listens at all, so the field appearance keeps Vuetify's own editing and
// no document listeners are added for it.
const syncTextAppearanceListeners = () => {
  removeTextAppearanceListeners()
  const root = autocompleteRef.value?.$el
  if (!isTextAppearance.value || !root) return
  document.addEventListener('pointerdown', markPointerInteraction, true)
  document.addEventListener('keydown', markKeyboardInteraction, true)
  root.addEventListener('keydown', blockSelectionClear, true)
  root.addEventListener('beforeinput', blockTextInput, true)
  textAppearanceRoot = root
}

onMounted(syncTextAppearanceListeners)
watch(isTextAppearance, syncTextAppearanceListeners)
onBeforeUnmount(removeTextAppearanceListeners)

const getLabel = computed(() => {
  return props.required ? props.label + '*' : props.label
})

// A text trigger has no room for a details row, so it always hides it; the field appearance keeps
// whatever the consumer passed (Vuetify's default when nothing was).
const resolvedHideDetails = computed(() => {
  if (isTextAppearance.value) return true
  return attrs.hideDetails ?? attrs['hide-details']
})

const isMultiple = () => {
  if (Object.prototype.hasOwnProperty.call(attrs, 'multiple')) {
    return attrs.multiple !== false
  }

  return false
}

const isReturnObject = () => {
  if (Object.prototype.hasOwnProperty.call(attrs, 'returnObject')) {
    return attrs.returnObject !== false
  }

  if (Object.prototype.hasOwnProperty.call(attrs, 'return-object')) {
    return attrs['return-object'] !== false
  }

  return false
}

const getValueComparator = (): WAutocompleteListItemValueComparator | undefined => {
  const comparator = attrs.valueComparator ?? attrs['value-comparator']
  return typeof comparator === 'function' ? (comparator as WAutocompleteListItemValueComparator) : undefined
}

const selectedValues = computed(() => {
  return Array.isArray(internalValue.value) ? internalValue.value : []
})

const getDropdownItems = () => {
  return Array.isArray(attrs.items) ? attrs.items : []
}

const getItemTitle = () => attrs.itemTitle ?? attrs['item-title']

const getItemValue = () => attrs.itemValue ?? attrs['item-value']

const autocompleteSelectionOptions = computed(() => ({
  itemTitle: getItemTitle(),
  itemValue: getItemValue(),
  returnObject: isReturnObject()
}))

const selectAllValues = computed(() =>
  getAutocompleteSelectAllValues(getDropdownItems(), autocompleteSelectionOptions.value)
)

const selectAllComparisonValues = computed(() =>
  getAutocompleteSelectAllComparisonValues(getDropdownItems(), autocompleteSelectionOptions.value)
)

const isEmpty = () => {
  if (isMultiple()) {
    return selectedValues.value.length === 0
  }
  return internalValue.value === null || internalValue.value === undefined || internalValue.value === ''
}

const isAllSelected = () => {
  return areAutocompleteValuesAllSelected(
    selectedValues.value,
    selectAllComparisonValues.value,
    autocompleteSelectionOptions.value,
    getValueComparator()
  )
}

const toggle = () => {
  if (isAllSelected()) {
    internalValue.value = []
  } else {
    internalValue.value = selectAllValues.value
  }
}

const selectionKey = (value: unknown, index: number) => {
  return typeof value === 'string' || typeof value === 'number' ? value : `selection-${index}`
}

const focus = () => {
  autocompleteRef.value?.focus?.()
}

const dropdownMenuStyles = {
  '--w-dropdown-menu-surface-bg': tokens.color.background.surface.default,
  '--w-dropdown-menu-border': tokens.color.input.border.default,
  '--w-dropdown-menu-hover-bg': tokens.color.background.surface.pressed,
  '--w-dropdown-menu-active-text': tokens.color.content.text.link
}

const computedMenuProps = computed(() => ({
  ...props.menuProps,
  contentClass: appendOverlayContentClasses(props.menuProps?.contentClass, [
    'v-autocomplete__content',
    'w-dropdown__content'
  ]),
  'data-testid': props.sectionTag ? `${props.sectionTag}-menu` : undefined,
  style: mergeOverlayTokenStyles(props.menuProps?.style, dropdownMenuStyles)
}))

defineExpose({ focus })
</script>

<style scoped lang="scss">
@include wasabi-outlined-field;

:deep(.v-field) {
  border-radius: v-bind('tokens.borderRadius.layer.small') !important;
}

:deep(.v-field--variant-outlined .v-label.v-field-label--floating) {
  margin-left: 0 !important;
}

:deep(.v-field:hover:not(.v-field--focused):not(.v-field--error):not(.v-field--disabled)) {
  background-color: v-bind('tokens.color.input.surface.hover') !important;
}

:deep(.v-field--disabled) {
  background-color: v-bind('tokens.color.input.surface.disabled') !important;
  color: v-bind('tokens.color.input.text.disabled') !important;
}

:deep(.v-field--error) {
  color: v-bind('tokens.color.input.text.error') !important;
}

:deep(.v-field--error > .v-field__field > .v-label),
:deep(.v-field--error > .v-field__outline > .v-field__outline__notch > .v-label) {
  color: v-bind('tokens.color.input.text.error') !important;
}

:global(.w-dropdown__content) {
  border: 1px solid var(--w-dropdown-menu-border) !important;
  max-height: 350px !important;
  overflow-x: hidden !important;
  overflow-y: hidden !important;
}

:global(.w-dropdown__content > .v-sheet) {
  display: flex;
  flex-direction: column;
  min-block-size: 0;
  min-inline-size: 0;
  overflow: hidden !important;
}

:global(.w-dropdown__content .v-list) {
  background-color: var(--w-dropdown-menu-surface-bg) !important;
  flex: 1 1 auto;
  min-block-size: 0;
  min-inline-size: 0;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}

:global(.w-dropdown__content > .v-sheet > footer) {
  flex: 0 0 auto;
  max-inline-size: 100%;
  min-inline-size: 0;
}

:global(.w-dropdown__content .v-list-item) {
  background-color: transparent !important;
}

:global(.w-dropdown__content .v-list-item > .v-list-item__content > .v-list-item-title) {
  font-weight: unset;
}

:global(.w-dropdown__content .v-list-item:hover) {
  background-color: var(--w-dropdown-menu-hover-bg) !important;

  .v-list-item__content > .v-list-item-title {
    font-weight: 700 !important;
  }
}

:global(.w-dropdown__content .v-list-item:focus) {
  background-color: var(--w-dropdown-menu-hover-bg) !important;

  .v-list-item__content > .v-list-item-title {
    font-weight: 700 !important;
  }
}

:global(.w-dropdown__content .v-list-item:focus-visible) {
  background-color: var(--w-dropdown-menu-hover-bg) !important;

  .v-list-item__content > .v-list-item-title {
    font-weight: 700 !important;
  }
}

:global(.w-dropdown__content .v-list-item > .v-list-item__content > .v-list-item-title) {
  font-size: 14px !important; /* TODO: typography class not applicable for Vuetify override */
}

:global(.w-dropdown__content .v-list-item.v-list-item--active) {
  background-color: transparent !important;
  color: var(--w-dropdown-menu-active-text) !important;
  font-weight: 600 !important;
}

:global(.w-dropdown__content .v-list-item.v-list-item--active:hover),
:global(.w-dropdown__content .v-list-item.v-list-item--active:focus),
:global(.w-dropdown__content .v-list-item.v-list-item--active:focus-visible) {
  background-color: var(--w-dropdown-menu-hover-bg) !important;
}

:global(.w-dropdown__content .v-list-item > .v-list-item__overlay) {
  background: transparent !important;
  opacity: 0 !important;
}

:global(.v-field__append-inner > .v-icon),
:global(.v-selection-control__input > .v-icon) {
  opacity: 1 !important;
}

:deep(.v-field__input) {
  line-height: 24px;
  min-height: max(
    48px,
    1rem + var(--v-field-input-padding-top) + var(--v-field-input-padding-bottom) +
      var(--v-input-chips-margin-bottom, 0px) + 1px
  );
  padding-bottom: 4px;
  padding-top: 20px;
  flex-wrap: nowrap !important;
  overflow: hidden;
}

:deep(.v-field__field) {
  min-height: 48px;
}

:deep(.v-autocomplete__selection) {
  opacity: 1 !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.placeholder-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.v-field__input > input) {
  min-height: 10px;
  height: 10px;
  opacity: 0;
  cursor: pointer;
}

.v-autocomplete--active-menu :deep(.v-field__input > input) {
  opacity: 1;
  height: auto;
  min-height: auto;
}

.v-autocomplete--active-menu:not(.w-dropdown--multiple) :deep(.v-autocomplete__selection),
.v-autocomplete--active-menu.w-dropdown--searching :deep(.v-autocomplete__selection) {
  display: none;
}

.v-autocomplete--active-menu :deep(.v-field__input > input) {
  padding-top: 0 !important;
  align-self: center;
}

:deep(.v-text-field__prefix),
:deep(.v-text-field__suffix),
:deep(.v-field__input),
:deep(.v-field.v-field) {
  cursor: pointer !important;
}

:deep(.v-input__details) {
  padding: 6px 0 0;
}

.input-message-icon {
  font-size: 12px;
}

.message-icon {
  font-size: 12px;
}

.no-label {
  :deep(.v-field__input) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
  }

  :deep(.v-field__input > input) {
    position: absolute !important;
    width: 0 !important;
    min-height: 0 !important;
    padding: 0 !important;
  }

  :deep(.v-field__field) {
    min-height: 40px !important;
  }

  &.v-autocomplete--active-menu {
    :deep(.v-field__input > input) {
      position: static !important;
      width: auto !important;
      min-height: auto !important;
      padding: initial !important;
    }
  }
}

.extra-room {
  :deep(.v-field__input) {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }
}

.w-dropdown--medium.no-label {
  :deep(.v-input__control),
  :deep(.v-field),
  :deep(.v-field__field),
  :deep(.v-field__input) {
    height: v-bind('tokens.spacing.control.height.medium') !important;
    min-height: v-bind('tokens.spacing.control.height.medium') !important;
  }

  :deep(.v-field__input) {
    padding-bottom: 0 !important;
    padding-top: 10px !important;
  }

  :deep(.v-field__append-inner),
  :deep(.v-field__prepend-inner) {
    align-items: center;
    height: v-bind('tokens.spacing.control.height.medium');
    padding-top: 0;
  }

  :deep(.v-field__input) {
    padding-bottom: 0 !important;
    padding-top: 0 !important;
  }
}

// Text appearance — a bare selection label + chevron sized to its content, for inline period /
// sort pickers that read as text rather than as a form field. Declared last so its overrides win
// against the labeled and no-label field rules above (equal specificity, later source order).
.w-dropdown--text {
  display: inline-flex;
  flex: 0 0 auto;
  width: auto;
  min-width: 0;

  :deep(.v-input__control),
  :deep(.v-field),
  :deep(.v-field__field),
  :deep(.v-field__input) {
    height: auto !important;
    min-height: 0 !important;
  }

  // The hover rule above matches the same :not() chain, so this one has to repeat it to win.
  :deep(.v-field),
  :deep(.v-field:hover:not(.v-field--focused):not(.v-field--error):not(.v-field--disabled)) {
    --v-field-padding-start: 0;
    --v-field-padding-end: 0;
    padding: 0 !important;
    border-radius: v-bind('tokens.borderRadius.layer.small') !important;
    background-color: transparent !important;
    font-size: var(--type-size-s) !important; /* matches label-semibold-small on the selection */
  }

  :deep(.v-field__outline) {
    display: none !important;
  }

  :deep(.v-field__input) {
    flex: 0 0 auto;
    width: auto;
    padding: 0 !important;
    gap: 0;
    line-height: var(--type-lh-s);
    color: v-bind('tokens.color.content.text.secondary');
  }

  // Vuetify caps a selection at calc(100% - 4px) of the field; a content-sized field would clip
  // its own label by those 4px.
  :deep(.v-autocomplete__selection) {
    flex: 0 0 auto;
    max-width: none;
    margin: 0;
  }

  :deep(.v-field__append-inner) {
    height: auto;
    min-height: 0;
    margin-inline-start: v-bind('tokens.spacing.gap.icon');
    padding: 0 !important;
    align-items: center;
  }

  :deep(.v-field__append-inner .v-icon) {
    margin: 0 !important;
    font-size: 14px !important;
    color: v-bind('tokens.color.content.text.secondary') !important;
  }

  // Same states as WContainer's title dropdown: link color while hovered or open, and the DS
  // keyboard-focus outline instead of the outlined field's focus shadow. The outline only shows
  // while the menu is closed — the open state (link color + flipped chevron) is its own indicator,
  // and Chrome treats any focused text input as keyboard-focused, so :focus-visible cannot tell a
  // click from a Tab here.
  &:hover :deep(.v-field__input),
  &.v-autocomplete--active-menu :deep(.v-field__input) {
    color: v-bind('tokens.color.content.text.link');
  }

  &:hover :deep(.v-field__append-inner .v-icon),
  &.v-autocomplete--active-menu :deep(.v-field__append-inner .v-icon) {
    color: v-bind('tokens.color.content.text.link') !important;
  }

  :deep(.v-field--focused) {
    box-shadow: none;
  }

  // !important because the shared outlined-field mixin above resets the focused outline that way.
  &:not(.v-autocomplete--active-menu) :deep(.v-field--focused) {
    outline: 2px solid v-bind('tokens.color.focus.ring') !important;
    outline-offset: 2px;
  }

  // The selection stays visible and the search input stays hidden while the menu is open: a text
  // trigger reads as a label, never as a search field. `flex` is Vuetify's own value for the
  // selection wrapper, so the label keeps its line box instead of shifting up.
  &.v-autocomplete--active-menu :deep(.v-autocomplete__selection) {
    display: flex;
  }

  :deep(.v-field__input > input),
  &.v-autocomplete--active-menu :deep(.v-field__input > input) {
    position: absolute !important;
    width: 0 !important;
    min-width: 0 !important;
    height: 0 !important;
    min-height: 0 !important;
    padding: 0 !important;
    opacity: 0;
  }
}
</style>
